import { createTheme, ThemeProvider } from "@mui/material"
import { lazy, Suspense } from "react"
import { BrowserRouter } from "react-router-dom"
import { FakeHeader } from "./view/loading-app/FakeHeader"
import ReactGA from "react-ga4"

const sans = `"Nunito", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`
const serif = `"Caveat", "Patrick Hand", cursive`
const hand = `"Patrick Hand", "Caveat", cursive`

const theme = createTheme({
    palette: {
        primary: {
            main: "#2B6643",
            light: "#5AA879",
            dark: "#1d4a30",
            contrastText: "#fbfff7",
        },
        secondary: {
            main: "#EF476F",
            contrastText: "#fbfff7",
        },
        warning: {
            main: "#FF9F1C",
        },
        info: {
            main: "#118AB2",
        },
        success: {
            main: "#06D6A0",
        },
        background: {
            default: "#eef7ef",
            paper: "#fbfff7",
        },
        text: {
            primary: "#1c1c1c",
            secondary: "#5a6b5e",
        },
    },
    typography: {
        fontFamily: sans,
        h1: {
            fontFamily: serif,
            fontSize: "56px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            lineHeight: 1.1,
            paddingBottom: "10px",
            paddingTop: "10px",
            textAlign: "center",
        },
        h2: {
            fontFamily: serif,
            fontSize: "40px",
            fontWeight: 700,
            lineHeight: 1.15,
        },
        h3: {
            fontFamily: serif,
            fontSize: "32px",
            fontWeight: 700,
        },
        h4: {
            fontFamily: serif,
            fontSize: "28px",
            fontWeight: 700,
        },
        h5: {
            fontFamily: hand,
            fontWeight: 700,
            paddingBottom: "20px",
            textAlign: "center",
        },
        h6: {
            fontFamily: hand,
            fontWeight: 700,
        },
        button: {
            fontFamily: hand,
            fontWeight: 700,
            fontSize: "16px",
            letterSpacing: "0.5px",
            textTransform: "none",
        },
        body1: { fontFamily: sans, fontSize: "16px" },
        body2: { fontFamily: sans },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    border: "3px solid #1c1c1c",
                    borderRadius: "999px",
                    padding: "8px 18px",
                    boxShadow: "3px 3px 0 #1c1c1c",
                    transform: "rotate(-1deg)",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    "&:hover": {
                        transform: "rotate(0) translate(-1px, -1px)",
                        boxShadow: "4px 4px 0 #1c1c1c",
                    },
                },
                text: {
                    border: "none",
                    boxShadow: "none",
                    transform: "none",
                    "&:hover": { transform: "none", boxShadow: "none" },
                },
                containedPrimary: { background: "#2B6643", color: "#fbfff7" },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: "3px solid #1c1c1c",
                    boxShadow: "4px 4px 0 #1c1c1c",
                    borderRadius: 14,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: "3px solid #1c1c1c",
                    boxShadow: "4px 4px 0 #1c1c1c",
                    borderRadius: 14,
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: "#cc2e54",
                    textDecoration: "none",
                    borderBottom: "2px dashed rgba(204,46,84,0.45)",
                    "&:hover": { borderBottomStyle: "solid" },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: "rgba(28,28,28,0.25)", borderBottomWidth: 2 },
            },
        },
    },
})

function App() {
    const AppLazy = lazy(() =>
        import("./view/loading-app/AppLazy").then(({ AppLazy }) => ({ default: AppLazy }))
    )

    ReactGA.initialize("G-0PK48WJMNG", { testMode: process.env.NODE_ENV === "test" })
    ReactGA.send("pageview")

    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
                <Suspense fallback={<FakeHeader />}>
                    {/* lazy: otherwise the metamask isActivating always returns true */}
                    <AppLazy />
                </Suspense>
            </BrowserRouter>
        </ThemeProvider>
    )
}

export default App
